import React from 'react';
import './StupidSystemDesigns.css';

const eventSchedulerDesignCode = String.raw`
#include <iostream>
#include <vector>
#include <unordered_map>
#include <unordered_set>

using namespace std;

// --- NOTIFICATION SERVICE ---
class NotificationService {
public:
    void sendReminder(int userId, int eventId, int minutesLeft) {
        cout << "  [🔔 ALERT] User " << userId << " -> Event " << eventId 
             << " starts in " << minutesLeft << " mins!\n";
    }
};

// --- CORE ENTITIES ---
class User {
    int userId;
    unordered_set<int> events;
public:
    User(int id) : userId(id) {}
    void addEvent(int eventId) { events.insert(eventId); }
    void deleteEvent(int eventId) { events.erase(eventId); }
    const unordered_set<int>& getUserEvents() const { return events; }
};

struct Event {
    int eventId;
    int startTime;
    int endTime;
    vector<int> userList;

    Event(int id, int start, int end, vector<int> list) 
        : eventId(id), startTime(start), endTime(end), userList(list) {}
};

// --- SCHEDULER ---
class Scheduler {
    unordered_map<int, Event*> eventRepo;
    unordered_map<int, User*> userRepo;
    int eventIdCounter = 1;
    int userIdCounter = 1;
    NotificationService notifier;

public:
    ~Scheduler() {
        for (auto& pair : eventRepo) delete pair.second;
        for (auto& pair : userRepo) delete pair.second;
    }
    
    int addUser() {
        int id = userIdCounter++;
        userRepo[id] = new User(id);
        return id;
    }
    
    int addEvent(int startTime, int endTime, vector<int> userIdList) {
        for (int userId : userIdList) {
            if (!userRepo.count(userId) || !checkConflict(userId, startTime, endTime)) return -1;
        }
        
        int id = eventIdCounter++;
        Event* event = new Event(id, startTime, endTime, userIdList);
        
        for (int userId : userIdList) userRepo[userId]->addEvent(id);
        eventRepo[id] = event;
        return id;
    }
    
    bool deleteEvent(int eventId) {
        if (!eventRepo.count(eventId)) return false;
        Event* event = eventRepo[eventId];
        
        for (int userId : event->userList) {
            userRepo[userId]->deleteEvent(eventId);
        }
        eventRepo.erase(eventId);
        delete event;
        return true;
    }
    
    bool checkConflict(int userId, int startTime, int endTime) {
        for (int eventId : userRepo[userId]->getUserEvents()) {
            Event* event = eventRepo[eventId];
            if (event->startTime >= endTime || event->endTime <= startTime) continue;
            return false;
        }
        return true;
    }

    void processTick(int currentTime) {
        vector<int> expiredEvents;

        for (const auto& [eventId, event] : eventRepo) {
            // Check for upcoming notifications (15 mins advance)
            if (event->startTime == currentTime + 15) {
                for (int userId : event->userList) {
                    notifier.sendReminder(userId, eventId, 15);
                }
            }
            // Mark expired events for cleanup
            if (event->endTime <= currentTime) {
                expiredEvents.push_back(eventId);
            }
        }

        // Delete expired events safely
        for (int eventId : expiredEvents) {
            cout << "  [🧹 CLEANUP] Event " << eventId << " ended. Removing from system.\n";
            deleteEvent(eventId);
        }
    }

    void simulate(int maxTime) {
        cout << "\n=============================================\n";
        cout << " STARTING TIME SIMULATION (t=1 to t=" << maxTime << ")\n";
        cout << "=============================================\n";
        for (int t = 1; t <= maxTime; ++t) {
            bool hasAction = false;
            for (const auto& [id, ev] : eventRepo) {
                if (ev->startTime == t + 15 || ev->endTime == t) hasAction = true;
            }
            
            if (hasAction) {
                cout << "\n--- Time Tick: " << t << " --- \n";
                processTick(t);
            }
        }
        cout << "\n=============================================\n";
        cout << " SIMULATION ENDED.\n";
        cout << "=============================================\n";
    }
};

// --- DRIVER CODE ---
int main() {
    Scheduler sys;
    
    cout << "--- 1. SYSTEM SETUP ---\n";
    int alice = sys.addUser();   // User 1
    int bob = sys.addUser();     // User 2
    int charlie = sys.addUser(); // User 3
    cout << "Onboarded Users: Alice(" << alice << "), Bob(" << bob << "), Charlie(" << charlie << ")\n\n";

    cout << "--- 2. SCHEDULING EVENTS ---\n";
    
    // Scenario A: Standard booking
    int e1 = sys.addEvent(20, 30, {alice, bob});
    cout << "Event " << e1 << " (Team Sync) created for Alice & Bob (t=20 to t=30).\n";

    // Scenario B: Exact boundary overlap (Should SUCCEED)
    // Starts exactly when Event 1 ends. No conflict.
    int e2 = sys.addEvent(30, 45, {bob, charlie});
    if (e2 != -1) {
        cout << "Event " << e2 << " (Bob & Charlie 1-on-1) created (t=30 to t=45). Boundary check passed!\n";
    }

    // Scenario C: Hard Conflict (Should FAIL)
    // Charlie tries to book Alice during Event 1
    int e3 = sys.addEvent(25, 35, {alice, charlie});
    if (e3 == -1) {
        cout << "[❌ REJECTED] Failed to create Event for Alice & Charlie (t=25 to t=35). Conflict detected for Alice!\n";
    }

    // Scenario D: Solo event to be manually deleted later
    int e4 = sys.addEvent(10, 15, {alice});
    cout << "Event " << e4 << " (Alice Solo Focus) created (t=10 to t=15).\n\n";

    cout << "--- 3. MANUAL CANCELLATIONS ---\n";
    if (sys.deleteEvent(e4)) {
        cout << "Alice manually canceled Event " << e4 << " before it started.\n";
    }

    // --- 4. RUN TIME CLOCK ---
    // Will trigger reminders 15 mins before e1 and e2, and clean them up when they end.
    sys.simulate(50);

    return 0;
}
`;

function EventSchedulerDesign({ theme, onBackClick }) {
    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Event Scheduler Design</h1>
                            <p className="system-designs-subtitle">
                                A simple template for a conflict-free event scheduling system with notifications and cleanup.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Requirements</h2>
                            <ul>
                                <li>Users can create, edit and delete events</li>
                                <li>Events can have multiple users in recipient lists</li>
                                <li><b>Users cannot have overlapping events</b></li>
                                <li>Run a notification 15 minutes before events, and a cleanup job for expired events</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Requirements Satisfied</h2>
                            <ul>
                                <li><strong>O(1) State Management:</strong> Use <code>unordered_map</code> for fast inserts and lookups for users and events.</li>
                                <li><strong>Conflict Prevention:</strong> Detect overlapping events in real time before committing any state changes.</li>
                                <li><strong>Relational Consistency:</strong> Maintain two-way bindings so users know their events and events know their users, with safe rollback if an update fails.</li>
                                <li><strong>Temporal Awareness:</strong> Simulate notifications and garbage collection of expired events through a decoupled, tick-based clock.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Core Logic Used</h2>
                            <ul>
                                <li><strong>Decoupled Architecture:</strong> The <code>Scheduler</code> orchestrates interactions while delegating specific duties, such as notifications, to distinct components.</li>
                                <li><strong>Graph-like Adjacency:</strong> The <code>User</code> class uses an <code>unordered_set</code> of event IDs as an adjacency list for O(1) lookups.</li>
                                <li><strong>Interval Overlap Math:</strong> Intervals <code>[S1, E1]</code> and <code>[S2, E2]</code> conflict unless one ends before the other starts: <code>E1 &lt;= S2</code> or <code>S1 &gt;= E2</code>.</li>
                                <li><strong>Iterator-Safe Deletions:</strong> During time ticks, collect expired IDs in a temporary vector before deleting them, preventing map mutation during iteration.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>Working Logic</h2>
                            </div>
                            <pre className="design-code-block"><code>{eventSchedulerDesignCode}</code></pre>
                        </div>

                        <div className="design-section stupid-design-section">
                            <h2>Stupid Anti-Patterns From The Code - trying a new format for this page :)</h2>
                            <ul>
                                <li> In <code>processTick</code>, remove the check for <code>event-&gt;startTime == currentTime + 15</code>. Once a meeting is booked, <code>NotificationService</code> screams at you every tick: <em>&quot;Starts in 400 mins! Starts in 399 mins! Starts in 398 mins!&quot;</em> until the event finally happens.</li>
                                <li> In <code>addEvent</code>, replace <code>new Event(...)</code> with one global <code>Event</code> object. Every new booking overwrites that object, morphing Alice&apos;s 10:00 AM sync into Charlie&apos;s 2:00 PM solo focus time and dragging Alice and Bob along with it.</li>
                                <li> Instead of having <code>checkConflict</code> return <code>false</code>, silently chop the existing event&apos;s <code>endTime</code> to make room for the new one. A meeting that gets interrupted at the five-minute mark simply ends early and kicks everyone out of the room.</li>
                                <li> Instead of ticking in order with <code>for (int t = 1; t &lt;= maxTime; ++t)</code>, use a random number generator to guess the current time. The loop can jump from <code>t=40</code> back to <code>t=2</code> and then forward to <code>t=500</code>, triggering notifications out of order and cleaning up meetings before they are created.</li>
                            </ul>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        Here is a joke i stole from reddit:<br></br><br></br> At my job we would mark our time "off" in a calendar with an event named, for example, "Robert Off".
                        <br></br>This was fine until Jack needed to go on leave.
                    </div>

                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default EventSchedulerDesign;